import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useMemo } from 'react';
export function StrategyConnections({ connections, selectedNodeId, onConnectionClick, animated = true }) {
    // Calculate SVG bounds
    const svgBounds = useMemo(() => {
        if (connections.length === 0) {
            return { minX: 0, minY: 0, maxX: 1000, maxY: 1000 };
        }
        let minX = Infinity;
        let minY = Infinity;
        let maxX = -Infinity;
        let maxY = -Infinity;
        connections.forEach(conn => {
            minX = Math.min(minX, conn.parentX, conn.childX);
            minY = Math.min(minY, conn.parentY, conn.childY);
            maxX = Math.max(maxX, conn.parentX + conn.parentW, conn.childX + conn.childW);
            maxY = Math.max(maxY, conn.parentY + conn.parentH, conn.childY + conn.childH);
        });
        return {
            minX: minX - 50,
            minY: minY - 50,
            maxX: maxX + 50,
            maxY: maxY + 50
        };
    }, [connections]);
    // Generate Bezier curve path from parent to child
    const generatePath = (conn) => {
        // Center points of nodes
        const parentCenterX = conn.parentX + conn.parentW / 2;
        const parentCenterY = conn.parentY + conn.parentH;
        const childCenterX = conn.childX + conn.childW / 2;
        const childCenterY = conn.childY;
        // Bezier control points
        const controlOffsetY = (childCenterY - parentCenterY) / 3;
        return `
      M ${parentCenterX} ${parentCenterY}
      C ${parentCenterX} ${parentCenterY + controlOffsetY},
        ${childCenterX} ${childCenterY - controlOffsetY},
        ${childCenterX} ${childCenterY}
    `;
    };
    // Determine stroke color based on type and selection state
    const getStrokeColor = (conn) => {
        if (conn.type === 'blocking')
            return '#EF4444'; // red-500
        if (conn.type === 'dependency')
            return '#F59E0B'; // amber-500
        if (selectedNodeId === conn.parentId || selectedNodeId === conn.childId) {
            return '#3B82F6'; // blue-500
        }
        return '#D1D5DB'; // gray-300
    };
    // Determine stroke width based on selection
    const getStrokeWidth = (conn) => {
        if (selectedNodeId === conn.parentId || selectedNodeId === conn.childId) {
            return 2.5;
        }
        return 1.5;
    };
    const width = svgBounds.maxX - svgBounds.minX;
    const height = svgBounds.maxY - svgBounds.minY;
    return (_jsxs("svg", { className: "absolute pointer-events-auto", width: width, height: height, viewBox: `${svgBounds.minX} ${svgBounds.minY} ${width} ${height}`, style: {
            left: `${svgBounds.minX}px`,
            top: `${svgBounds.minY}px`,
            zIndex: 5
        }, children: [_jsxs("defs", { children: [_jsx("marker", { id: "arrowChild", markerWidth: "10", markerHeight: "10", refX: "9", refY: "3", orient: "auto", markerUnits: "strokeWidth", children: _jsx("path", { d: "M0,0 L0,6 L9,3 z", fill: "#D1D5DB" }) }), _jsx("marker", { id: "arrowDependency", markerWidth: "10", markerHeight: "10", refX: "9", refY: "3", orient: "auto", markerUnits: "strokeWidth", children: _jsx("path", { d: "M0,0 L0,6 L9,3 z", fill: "#F59E0B" }) }), _jsx("marker", { id: "arrowBlocking", markerWidth: "10", markerHeight: "10", refX: "9", refY: "3", orient: "auto", markerUnits: "strokeWidth", children: _jsx("path", { d: "M0,0 L0,6 L9,3 z", fill: "#EF4444" }) }), animated && (_jsx("style", { children: `
            @keyframes dash {
              to {
                stroke-dashoffset: 0;
              }
            }
            .path-animated {
              stroke-dasharray: 5, 5;
              animation: dash 20s linear infinite;
            }
          ` }))] }), connections.map((conn, idx) => {
                const strokeColor = getStrokeColor(conn);
                const strokeWidth = getStrokeWidth(conn);
                const markerType = conn.type || 'child';
                const isSelected = selectedNodeId === conn.parentId || selectedNodeId === conn.childId;
                return (_jsxs("g", { children: [_jsx("path", { d: generatePath(conn), stroke: "#000", strokeWidth: strokeWidth + 1, fill: "none", opacity: "0.1", strokeLinecap: "round", strokeLinejoin: "round" }), _jsx("path", { d: generatePath(conn), stroke: strokeColor, strokeWidth: strokeWidth, fill: "none", markerEnd: `url(#arrow${markerType})`, strokeLinecap: "round", strokeLinejoin: "round", className: animated && !isSelected ? 'path-animated' : '', style: {
                                transition: `stroke ${isSelected ? '0.2s' : '0.3s'} ease-out`,
                                opacity: conn.isHighlighted === false ? 0.3 : 1,
                                cursor: 'pointer'
                            }, onClick: () => {
                                if (onConnectionClick) {
                                    onConnectionClick(conn.parentId, conn.childId);
                                }
                            } }), _jsx("path", { d: generatePath(conn), stroke: "transparent", strokeWidth: Math.max(8, strokeWidth + 5), fill: "none", className: "cursor-pointer hover:stroke-blue-300 hover:opacity-20", onClick: () => {
                                if (onConnectionClick) {
                                    onConnectionClick(conn.parentId, conn.childId);
                                }
                            }, style: {
                                pointerEvents: 'stroke'
                            } })] }, idx));
            })] }));
}
